import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck, Zap, Target, CheckCircle2, Star, Users, Gift, Sparkles } from 'lucide-react';
import { useMotionValue, useTransform, animate, useInView, AnimatePresence } from 'framer-motion';
import { UrgencyBanner } from './components/UrgencyBanner';
import { BonusCard } from './components/BonusCard';
import { Reviews } from './components/Reviews';
import { FAQ } from './components/FAQ';
import { WhatYouReceive } from './components/WhatYouReceive';
import { PortalTerapeutico } from './components/PortalTerapeutico';
import { ReikiBackground } from './components/ReikiBackground';
import { LavenderBranches } from './components/LavenderBranches';
import { HeroAmbientEffects } from './components/HeroAmbientEffects';
import { GiftBackground } from './components/GiftBackground';
import { LifetimeAccessBonus } from './components/LifetimeAccessBonus';
import { FloatingReikiSymbols } from './components/FloatingReikiSymbols';
function AnimatedCounter({
  from,
  to
}: {
  from: number;
  to: number;
}) {
  const count = useMotionValue(from);
  const rounded = useTransform(count, latest => {
    return Math.round(latest).toLocaleString('es-AR');
  });
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true
  });
  useEffect(() => {
    if (isInView) {
      const controls = animate(count, to, {
        duration: 1.5,
        ease: 'easeOut'
      });
      return controls.stop;
    }
  }, [count, to, isInView]);
  return <motion.span ref={ref} data-id="element-0">{rounded}</motion.span>;
}
const testimonials = [{
  image: 'https://cdn.magicpatterns.com/uploads/1y1bQtuJgFGCAvQdNRgBRr/ChatGPT_Image_19_may_2026,_14_11_19.webp'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/kGsfNr5RRLf5VRi4YfeMZN/ChatGPT_Image_19_may_2026,_14_12_24.webp'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/vMBqhduTkAY88ALqYDheNZ/ChatGPT_Image_19_may_2026,_14_13_56.webp'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/jiyPjkZE44H9g6ZRyV5RKt/ChatGPT_Image_19_may_2026,_14_15_35.webp'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/8mT9D7xuYz4azHJCLLSXU2/ChatGPT_Image_19_may_2026,_14_17_01.webp'
}];
const heroImages = ['https://cdn.magicpatterns.com/uploads/7hAbtPEv66tS7cRaYNE4r9/me_gusta_mucho_202604131502.png', 'https://cdn.magicpatterns.com/uploads/pYiKeNHymWKoebcvZXR1Ds/Diseno_sin_titulo_(1).png'];
export function App() {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [spots, setSpots] = useState(18);
  const [joinedCount, setJoinedCount] = useState(3265);
  const [viewers, setViewers] = useState(48);
  const [showSaleAnimations, setShowSaleAnimations] = useState(false);
  const [timeLeft, setTimeLeft] = useState(900); // 15 minutes in seconds
  const [selectedTier, setSelectedTier] = useState<'solo' | 'bundle' | 'full'>('solo');
  const [selectedOptional, setSelectedOptional] = useState<'radiestesia' | 'constelaciones' | 'tarot'>('radiestesia');
  useEffect(() => {
    const viewerInterval = setInterval(() => {
      setViewers(prev => prev + (Math.random() > 0.5 ? 1 : -1));
    }, 3000);
    return () => clearInterval(viewerInterval);
  }, []);
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  useEffect(() => {
    // Meta Pixel Integration
    const pixelId = '2048912638989861';
    // @ts-ignore
    if (!window.fbq) {
      // @ts-ignore
      !function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        b.head.appendChild(t);
      }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    }
    // @ts-ignore
    window.fbq('init', pixelId);
    // @ts-ignore
    window.fbq('track', 'PageView');
  }, []);
  const handleSale = () => {
    setSpots(prev => prev > 2 ? prev - 1 : prev);
    setJoinedCount(prev => prev + 1);
    setShowSaleAnimations(true);
    setTimeout(() => setShowSaleAnimations(false), 2000);
  };
  const nextTestimonial = () => {
    setCurrentTestimonial(prev => (prev + 1) % testimonials.length);
  };
  const prevTestimonial = () => {
    setCurrentTestimonial(prev => (prev - 1 + testimonials.length) % testimonials.length);
  };
  const currentDate = new Date().toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  const scrollToPurchase = () => {
    document.getElementById('purchase')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const tiers = [{
    id: 'solo',
    name: 'Solo Kit Profesional Reiki',
    originalPrice: '$59.990',
    price: '$19.990',
    description: 'Guía Reiki Terapéutico PDF + 5 Bonos',
    checkoutUrl: 'https://codex-labs.shop/cart/43426863579195:1'
  }, {
    id: 'bundle',
    name: 'Guía Reiki + 1 Kit Opcional',
    originalPrice: '$79.990',
    price: '$29.990',
    description: 'Elegí un kit adicional para complementar tu formación',
    checkoutUrl: 'https://codex-labs.shop/cart/43426863579195:1',
    badge: 'MÁS ELEGIDO'
  }, {
    id: 'full',
    name: 'PORTAL TERAPÉUTICO COMPLETO ✨',
    originalPrice: '$99.990',
    price: '$39.990',
    description: 'Acceso total: Reiki + Radiestesia + Constelaciones + Tarot + Todos sus bonos',
    checkoutUrl: 'https://codex-labs.shop/cart/43552261701691:1',
    badge: 'MEJOR VALOR 🔥'
  }];
  const optionalKits = [{
    id: 'radiestesia',
    name: 'Kit Radiestesia Profesional',
    icon: '🌿'
  }, {
    id: 'constelaciones',
    name: 'Kit Constelaciones Familiar',
    icon: '🪷'
  }, {
    id: 'tarot',
    name: 'Kit Tarot Terapéutico',
    icon: '🔮'
  }];
  return <div className="min-h-screen bg-violet-50 text-violet-950 font-sans selection:bg-violet-200 selection:text-violet-950" data-id="element-1">
      <ReikiBackground data-id="element-2" />
      <LavenderBranches data-id="element-3" />
      <UrgencyBanner spots={spots} onSale={handleSale} viewers={viewers} data-id="element-4" />

      {/* Floating Reiki Symbols Overlay — covers first 3 sections */}
      <div className="relative" data-id="element-5">
        <FloatingReikiSymbols data-id="element-6" />

        {/* Hero Section */}
        <header className="relative overflow-hidden bg-[#0f051a]" data-id="element-7">
          <HeroAmbientEffects data-id="element-8" />
          <div className="relative" data-id="element-9">
            <img src="https://cdn.magicpatterns.com/uploads/8PJVHr7XDEt8fE6sq7jr7F/ChatGPT_Image_19_may_2026,_13_41_34.png" alt="Convertite en Terapéuta de Reiki Holístico Hoy" className="w-full h-auto block" data-id="element-10" />

            {/* Premium CTA Button Overlay */}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 z-20 w-full max-w-4xl px-4 text-center" data-id="element-11">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.5
            }} className="flex flex-col items-center relative" data-id="element-12">
                {/* Premium Breathing Glow Behind CTA */}
                <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[120%] bg-gradient-to-r from-[#D4AF37]/20 via-[#A78BFA]/20 to-[#D4AF37]/20 blur-2xl rounded-full pointer-events-none" animate={{
                opacity: [0.3, 0.6, 0.3],
                scale: [0.9, 1.1, 0.9]
              }} transition={{
                duration: 6,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-13" />

                <motion.button onClick={scrollToPurchase} animate={{
                y: [0, -8, 0],
                boxShadow: ['0 10px 25px -5px rgba(167, 139, 250, 0.3)', '0 20px 35px -5px rgba(212, 175, 55, 0.4)', '0 10px 25px -5px rgba(167, 139, 250, 0.3)']
              }} transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="relative group px-6 py-2 rounded-full bg-gradient-to-r from-[#D4AF37] via-[#F9E29C] to-[#A78BFA] text-violet-950 font-black text-sm md:text-base tracking-wider uppercase border border-white/40 overflow-hidden transition-transform active:scale-95 shadow-2xl pt-[15px] pb-[15px]" data-id="element-14">
                  {/* Glossy Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-transparent to-black/5 pointer-events-none" data-id="element-15" />

                  {/* Shine Effect */}
                  <motion.div animate={{
                  x: ['-100%', '200%']
                }} transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'linear',
                  repeatDelay: 2
                }} className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent -skew-x-12 pointer-events-none" data-id="element-16" />

                  <span className="relative z-10 flex items-center gap-3" data-id="element-17">
                    QUIERO APRENDER REIKI AHORA
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-18" />
                  </span>
                </motion.button>

                <p className="text-violet-900/60 text-[10px] md:text-xs font-bold tracking-wide mt-2" data-id="element-19">
                  Acceso inmediato • Descarga digital • Garantía 7 días
                </p>
              </motion.div>
            </div>
          </div>
          <img src="https://cdn.magicpatterns.com/uploads/1sqUCe4p6m43Fo88L4YnSx/59739290-0956-4546-8094-e44f3a477244.webp" alt="Metodología de Sanación" className="w-full h-auto block" data-id="element-20" />
          <img src="https://cdn.magicpatterns.com/uploads/uZWSBfCj1zJbPEd53naqFG/2f7a31b2-a71d-4d35-a1ba-5be39e544051.webp" alt="Arsenal Operativo" className="w-full h-auto block" data-id="element-21" />
        </header>

        <WhatYouReceive data-id="element-22" />

        <PortalTerapeutico data-id="element-23" />

        {/* Testimonials */}
        <section className="py-32 relative overflow-hidden bg-gradient-to-b from-violet-50 to-pink-50 pt-[5px] pb-[5px]" data-id="element-24">
          {/* Soft blur background elements */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-pink-100/80 via-transparent to-transparent pointer-events-none" data-id="element-25" />
          <div className="absolute -left-40 top-40 w-96 h-96 bg-pink-200/20 rounded-full blur-3xl pointer-events-none" data-id="element-26" />
          <div className="absolute -right-40 bottom-20 w-96 h-96 bg-pink-100 rounded-full blur-3xl shadow-[0_0_100px_rgba(219,39,119,0.1)] pointer-events-none" data-id="element-27" />

          <div className="max-container px-4 relative z-10" data-id="element-28">
            <div className="text-center mb-16 max-w-3xl mx-auto relative" data-id="element-29">
              <div className="absolute -top-8 left-0 md:-left-8 text-yellow-400 opacity-60" data-id="element-30">
                <Sparkles size={32} fill="currentColor" data-id="element-31" />
              </div>
              <span className="text-violet-600 font-black uppercase tracking-[0.3em] mb-6 block text-xs md:text-sm" data-id="element-32">
                Testimonios Reales
              </span>
              <h3 className="text-4xl md:text-7xl text-violet-950 tracking-tight font-medium font-['Playfair_Display'] mb-8 leading-[1.1]" data-id="element-33">
                <span className="text-violet-600 italic" data-id="element-34">Transforma</span> tus
                <br data-id="element-35" />
                sesiones hoy
              </h3>
              <p className="text-violet-400 font-medium text-lg md:text-xl leading-relaxed max-w-2xl mx-auto" data-id="element-36">
                Mira cómo terapeutas como tú ya están logrando resultados
                rápidos y profundos con nuestras herramientas.
              </p>
            </div>

            <div className="max-w-4xl mx-auto relative" data-id="element-37">
              <div className="text-center mb-6 md:hidden" data-id="element-38">
                <p className="text-violet-400 text-[10px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2" data-id="element-39">
                  <ArrowRight size={12} className="rotate-180" data-id="element-40" />
                  Deslizá para ver más
                  <ArrowRight size={12} data-id="element-41" />
                </p>
              </div>

              <div className="overflow-hidden" data-id="element-42">
                <motion.div key={currentTestimonial} drag="x" dragConstraints={{
                left: 0,
                right: 0
              }} onDragEnd={(_, info) => {
                if (info.offset.x > 100) {
                  prevTestimonial();
                } else if (info.offset.x < -100) {
                  nextTestimonial();
                }
              }} initial={{
                opacity: 0,
                x: 100
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -100
              }} transition={{
                duration: 0.5,
                ease: 'easeOut'
              }} className="bg-white p-4 md:p-8 rounded-[2rem] shadow-[0_30px_60px_-12px_rgba(219,39,119,0.15)] border border-pink-100 relative backdrop-blur-sm cursor-grab active:cursor-grabbing" data-id="element-43">
                  <div className="flex flex-col items-center gap-6" data-id="element-44">
                    <div className="flex justify-center text-pink-500 gap-1" data-id="element-45">
                      {[...Array(5)].map((_, idx) => <Star key={idx} size={24} fill="currentColor" strokeWidth={1} data-id="element-46" />)}
                    </div>

                    <div className="w-full max-w-lg overflow-hidden rounded-2xl border border-violet-100 shadow-inner" data-id="element-47">
                      <img src={testimonials[currentTestimonial].image} alt="Testimonio" className="w-full h-auto object-contain" data-id="element-48" />
                    </div>

                    <div className="text-center" data-id="element-49">
                      <span className="inline-block bg-pink-100 text-pink-700 text-[10px] font-black uppercase tracking-widest px-4 py-1.5 rounded-full" data-id="element-50">
                        Experiencia Real Verificada
                      </span>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Navigation Arrows */}
              <button onClick={prevTestimonial} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 bg-white p-4 rounded-full shadow-xl text-pink-600 hover:bg-pink-50 transition-colors z-20 hidden md:block" data-id="element-51">
                <ArrowRight className="rotate-180" size={24} data-id="element-52" />
              </button>
              <button onClick={nextTestimonial} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 bg-white p-4 rounded-full shadow-xl text-pink-600 hover:bg-pink-50 transition-colors z-20 hidden md:block" data-id="element-53">
                <ArrowRight size={24} data-id="element-54" />
              </button>

              {/* Pagination Dots */}
              <div className="flex justify-center gap-3 mt-12" data-id="element-55">
                {testimonials.map((_, i) => <button key={i} onClick={() => setCurrentTestimonial(i)} className={`h-3 rounded-full transition-all duration-300 ${currentTestimonial === i ? 'w-12 bg-pink-500' : 'w-3 bg-pink-200 hover:bg-pink-300'}`} data-id="element-56" />)}
              </div>
            </div>
          </div>
        </section>

        {/* Bonuses */}
        <section className="py-24 bg-violet-50 pt-[40px] pb-[40px] relative overflow-hidden" data-id="element-57">
          <GiftBackground data-id="element-58" />

          <div className="max-container px-4 relative z-10" data-id="element-59">
            <div className="text-center mb-16" data-id="element-60">
              <motion.div animate={{
              rotate: [-8, 8, -8],
              scale: [1, 1.1, 1]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="inline-block mb-6 text-violet-600" data-id="element-61">
                <Gift size={80} strokeWidth={1.5} data-id="element-62" />
              </motion.div>

              <h3 className="md:text-7xl text-violet-950 tracking-tight text-[42px] font-['Playfair_Display'] mb-6 font-[500] leading-tight" data-id="element-63">
                ¡Regalos{' '}
                <span className="text-violet-600 italic" data-id="element-64">Exclusivos!</span>
              </h3>

              <motion.div initial={{
              scale: 0.9,
              opacity: 0
            }} whileInView={{
              scale: 1,
              opacity: 1
            }} viewport={{
              once: true
            }} className="inline-flex items-center gap-3 px-8 py-3 rounded-full bg-violet-600 text-white text-sm md:text-lg font-black uppercase tracking-[0.25em] shadow-2xl shadow-violet-300/50" data-id="element-65">
                <Sparkles size={20} className="animate-pulse text-yellow-300" data-id="element-66" />
                <span data-id="element-67">100% GRATIS SOLO POR HOY</span>
                <Sparkles size={20} className="animate-pulse text-yellow-300" data-id="element-68" />
              </motion.div>
            </div>

            <div className="grid md:grid-cols-3 gap-8" data-id="element-69">
              <BonusCard number="01" title="Secretos Ancestrales de los 7 Chakras" image="https://cdn.magicpatterns.com/uploads/uMgyUyUPnGQJyaWFBVn3Vw/Haceme_libro_con_202604081850.jpg" description="Identifica la raíz emocional de cada síntoma físico en segundos." value="$47 USD" data-id="element-70" />
              <BonusCard number="02" title="Mapa de Bloqueos Emocionales y síntomas Físicos" image="https://cdn.magicpatterns.com/uploads/eQ7YVMBJDTVz6QqtMmLpkG/hacer_imagen_estilo_202604081854.jpg" description="Guiones listos para profundizar la sanación entre sesiones." value="$37 USD" data-id="element-71" />
              <BonusCard number="03" title="Biblioteca Secreta de las 50 Meditaciones de Poder" image="https://cdn.magicpatterns.com/uploads/aHbwXvGeGeLDCjHD3NmknJ/hacer_imagen_estilo_202604081856.jpg" description="Aprende a no absorber la densidad energética de tus pacientes." value="$27 USD" data-id="element-72" />
              <BonusCard number="04" title="Jornada Energética para Activar tu Potencial Divino" image="https://cdn.magicpatterns.com/uploads/ibkw9fw15obqjcufgV8F17/necesito_hacer_esta_202604131807.png" description="Métodos simples y eficaces de protección y limpieza energética para mantener tu vibración siempre elevada." value="$37 USD" data-id="element-73" />
              <BonusCard number="05" title="PROTOCOLO MAGNÉTICO DE MANIFESTACIÓN" image="https://cdn.magicpatterns.com/uploads/f6i2T5L2vzqUXnjckrx5Kn/hacer_imagen_estilo_202604081859.jpg" description="Herramienta poderosa para alinear tus deseos, elevar tu frecuencia y manifestar con claridad y propósito." value="$27 USD" data-id="element-74" />
            </div>
          </div>
        </section>
      </div>

      {/* Purchase Section */}
      <section id="purchase" className="py-24 bg-violet-100 relative overflow-hidden pt-[30px] pb-[30px]" data-id="element-75">
        <div className="max-w-4xl mx-auto px-4 relative z-10 space-y-[15px]" data-id="element-76">
          <div className="bg-violet-950 text-white p-4 text-center font-black uppercase tracking-widest mb-8 animate-pulse" data-id="element-77">
            OFERTA VÁLIDA SOLO POR HOY: {currentDate}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12" data-id="element-78">
            {['Descargue todos los archivos en formato PDF, guárdelos en una carpeta segura y téngalos para siempre.', 'Pago único y descarga inmediata.', 'Materiales listos para impresión.', 'Herramientas desarrolladas por terapeutas especialistas.'].map((text, i) => <div key={i} className="flex items-center gap-3 bg-white/50 backdrop-blur-sm p-4 rounded-2xl border border-violet-200 shadow-sm" data-id="element-79">
                <CheckCircle2 className="text-violet-600 shrink-0" size={20} data-id="element-80" />
                <p className="text-violet-900 text-sm font-bold leading-tight" data-id="element-81">
                  {text}
                </p>
              </div>)}
          </div>

          <div className="max-w-2xl mx-auto" data-id="element-82">
            <div className="bg-white border-[6px] border-violet-950 rounded-[3rem] shadow-2xl overflow-hidden" data-id="element-83">
              <div className="p-0" data-id="element-84">
                <div className="p-8 md:p-12 text-center pt-[0px] pb-[0px] pl-[5px] pr-[5px] space-y-[15px]" data-id="element-85">
                  <div className="pt-[15px] space-y-[0px]" data-id="element-86">
                    <p className="text-violet-400 text-xs font-black uppercase tracking-[0.2em]" data-id="element-87">
                      + {joinedCount} ALUMNOS
                    </p>
                    <div className="flex flex-col items-center gap-[0px]" data-id="element-88">
                      <div className="flex text-yellow-400 gap-1" data-id="element-89">
                        {[...Array(5)].map((_, i) => <Star key={i} size={24} fill="currentColor" data-id="element-90" />)}
                      </div>
                      <span className="text-violet-300 text-sm font-bold" data-id="element-91">
                        (1.728 reseñas)
                      </span>
                    </div>
                  </div>

                  <div className="space-y-6" data-id="element-92">
                    <div className="inline-block bg-amber-50 border border-amber-100 px-4 py-1.5 rounded-full pt-[0px] pb-[0px]" data-id="element-93">
                      <span className="md:text-xs font-black text-amber-700 uppercase tracking-[0.2em] text-[9px]" data-id="element-94">
                        ECOSISTEMA TERAPÉUTICO PROFESIONAL
                      </span>
                    </div>

                    <h3 className="text-3xl md:text-5xl font-medium tracking-tight text-violet-950 leading-[1.1] font-['Playfair_Display']" data-id="element-95">
                      Elegí el camino terapéutico{' '}
                      <span className="text-amber-500 italic" data-id="element-96">
                        que mejor conecte con vos
                      </span>{' '}
                      ✨
                    </h3>

                    <p className="text-violet-400 text-sm md:text-base font-medium leading-relaxed max-w-lg mx-auto" data-id="element-97">
                      Accedé de forma inmediata a nuestros programas
                      terapéuticos profesionales en{' '}
                      <span className="font-bold text-violet-600" data-id="element-98">
                        formato digital PDF
                      </span>{' '}
                      con acceso de por vida, material descargable y futuras
                      actualizaciones incluidas.
                    </p>
                  </div>

                  <div className="pt-8 pb-2" data-id="element-99">
                    <p className="text-[10px] font-black text-violet-400 uppercase tracking-[0.2em] flex items-center justify-center gap-2" data-id="element-100">
                      <ArrowRight size={12} className="rotate-90" data-id="element-101" />
                      Seleccioná el pack que prefieras:
                      <ArrowRight size={12} className="rotate-90" data-id="element-102" />
                    </p>
                  </div>

                  {/* Upsell Selector */}
                  <div className="space-y-3 text-left max-w-xl mx-auto py-6 pb-[0px] pt-[0px]" data-id="element-103">
                    {tiers.map(tier => <div key={tier.id} className="relative" data-id="element-104">
                        <button onClick={() => setSelectedTier(tier.id as any)} className={`w-full p-5 rounded-2xl border-2 transition-all duration-300 flex flex-col gap-1 relative overflow-hidden ${selectedTier === tier.id ? 'border-violet-600 bg-violet-50/50 shadow-lg' : 'border-violet-100 bg-white hover:border-violet-200'}`} data-id="element-105">
                          {tier.badge && <motion.div animate={tier.id === 'full' ? {
                        scale: [1, 1.05, 1],
                        backgroundColor: ['#7c3aed', '#8b5cf6', '#7c3aed']
                      } : {}} transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut'
                      }} className={`absolute top-0 right-0 text-white text-[8px] font-black px-3 py-1 rounded-bl-xl uppercase tracking-widest flex items-center gap-1 ${tier.id === 'bundle' ? 'bg-amber-500' : 'bg-violet-600'}`} data-id="element-106">
                              {tier.id === 'bundle' && <Star size={8} fill="currentColor" data-id="element-107" />}
                              {tier.badge}
                            </motion.div>}
                          <div className="flex justify-between items-center" data-id="element-108">
                            <div className="flex items-center gap-3" data-id="element-109">
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedTier === tier.id ? 'border-violet-600' : 'border-violet-200'}`} data-id="element-110">
                                {selectedTier === tier.id && <div className="w-2.5 h-2.5 bg-violet-600 rounded-full" data-id="element-111" />}
                              </div>
                              <span className={`font-black text-sm md:text-base ${selectedTier === tier.id ? 'text-violet-950' : 'text-violet-400'}`} data-id="element-112">
                                {tier.name}
                              </span>
                            </div>
                            <div className="flex flex-col items-end" data-id="element-113">
                              <span className="text-[10px] md:text-xs text-violet-300 line-through font-bold leading-none mb-1" data-id="element-114">
                                {tier.originalPrice}
                              </span>
                              <span className="font-black text-lg md:text-xl text-[#319E0D] leading-none" data-id="element-115">
                                {tier.price}
                              </span>
                            </div>
                          </div>
                          <p className="text-xs text-violet-400 font-medium ml-8" data-id="element-116">
                            {tier.description}
                          </p>
                        </button>

                        {/* Optional Kit Selector for Bundle */}
                        <AnimatePresence data-id="element-117">
                          {tier.id === 'bundle' && selectedTier === 'bundle' && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} className="overflow-hidden bg-white border-x-2 border-b-2 border-violet-600 rounded-b-2xl -mt-2 p-4 pt-6 space-y-3" data-id="element-118">
                                <p className="text-[10px] font-black text-violet-400 uppercase tracking-widest mb-2" data-id="element-119">
                                  Seleccioná tu kit adicional:
                                </p>
                                <div className="grid grid-cols-1 gap-2" data-id="element-120">
                                  {optionalKits.map(kit => <button key={kit.id} onClick={() => setSelectedOptional(kit.id as any)} className={`flex items-center justify-between p-3 rounded-xl border transition-all ${selectedOptional === kit.id ? 'border-violet-600 bg-violet-50 text-violet-950' : 'border-violet-100 text-violet-400 hover:border-violet-200'}`} data-id="element-121">
                                      <div className="flex items-center gap-2 text-xs font-bold" data-id="element-122">
                                        <span data-id="element-123">{kit.icon}</span>
                                        <span data-id="element-124">{kit.name}</span>
                                      </div>
                                      {selectedOptional === kit.id && <CheckCircle2 size={14} className="text-violet-600" data-id="element-125" />}
                                    </button>)}
                                </div>
                              </motion.div>}
                        </AnimatePresence>
                      </div>)}
                  </div>

                  <div className="relative" data-id="element-126">
                    <img src={selectedTier === 'full' ? 'https://cdn.magicpatterns.com/uploads/k5jkKkyGGauVHwLTsMd8cg/ChatGPT_Image_27_may_2026,_15_23_37.webp' : selectedTier === 'bundle' && selectedOptional === 'radiestesia' ? 'https://cdn.magicpatterns.com/uploads/dNAJTQKHMHTekLFmdtvV8j/2b00f44c-c4f2-49cd-a351-131ac0bd849f.webp' : selectedTier === 'bundle' && selectedOptional === 'constelaciones' ? 'https://cdn.magicpatterns.com/uploads/qwsd19i1d8sLWwWWVcAtqJ/ChatGPT_Image_27_may_2026,_15_37_22.webp' : selectedTier === 'bundle' && selectedOptional === 'tarot' ? 'https://cdn.magicpatterns.com/uploads/kvTGsrCAGV8Kf5DT8RUnUv/27_may_2026,_15_44_23.webp' : 'https://cdn.magicpatterns.com/uploads/2o9ob7Pr65Uv9j9Qq3pQW4/7089dce0-3f7b-48f1-8d15-e50ff501487c.webp'} alt="Paquete Completo Reiki Terapéutico" className="w-full h-auto" data-id="element-127" />
                  </div>

                  <div className="space-y-4" data-id="element-128">
                    <div className="relative overflow-hidden bg-violet-50 border border-violet-100 rounded-2xl p-4 shadow-sm max-w-sm mx-auto group" data-id="element-129">
                      {/* Shimmer Effect */}
                      <motion.div animate={{
                      x: ['-100%', '200%']
                    }} transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      repeatDelay: 1.5,
                      ease: 'easeInOut'
                    }} className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent -skew-x-12 pointer-events-none z-10" data-id="element-130" />

                      <div className="relative z-20 flex flex-col items-center gap-2" data-id="element-131">
                        <div className="inline-flex items-center gap-2 px-4 py-1 bg-violet-600 text-white rounded-full font-black uppercase tracking-widest shadow-md text-[10px]" data-id="element-132">
                          <Sparkles size={14} className="text-yellow-300" data-id="element-133" />
                          66% OFF HOY
                        </div>
                        <p className="text-violet-950 font-bold text-lg" data-id="element-134">
                          3 cuotas sin interés de{' '}
                          <span className="text-[#319E0D]" data-id="element-135">
                            $
                            {Math.round((selectedTier === 'solo' ? 19990 : selectedTier === 'bundle' ? 29990 : 39990) / 3).toLocaleString('es-AR')}
                          </span>
                        </p>
                      </div>
                    </div>

                    <div className="relative group" data-id="element-136">
                      {/* Magical Glow Background */}
                      <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400 via-green-400 to-yellow-400 rounded-2xl blur opacity-30 group-hover:opacity-60 transition duration-1000 group-hover:duration-200 animate-pulse" data-id="element-137"></div>

                      <a href={selectedTier === 'bundle' && selectedOptional === 'radiestesia' ? 'https://codex-labs.shop/cart/43552268189755:1' : selectedTier === 'bundle' && selectedOptional === 'constelaciones' ? 'https://codex-labs.shop/cart/43552280313915:1' : selectedTier === 'bundle' && selectedOptional === 'tarot' ? 'https://codex-labs.shop/cart/43552284147771:1' : tiers.find(t => t.id === selectedTier)?.checkoutUrl} target="_blank" rel="noopener noreferrer" className="relative w-full bg-[#82C91E] text-white py-6 rounded-2xl font-black flex items-center justify-center gap-3 hover:bg-[#74b816] transition-all shadow-[0_10px_20px_rgba(130,201,30,0.3)] hover:scale-[1.02] active:scale-95 uppercase tracking-tight overflow-hidden text-[28px]" data-id="element-138">
                        {/* Shimmer Effect */}
                        <motion.div animate={{
                        x: ['-100%', '200%']
                      }} transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: 'linear',
                        repeatDelay: 1
                      }} className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12" data-id="element-139" />
                        ACCEDER AHORA
                      </a>
                    </div>

                    <div className="flex flex-col items-center gap-4 py-4" data-id="element-140">
                      <motion.div animate={{
                      scale: [1, 1.05, 1],
                      opacity: [1, 0.8, 1]
                    }} transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut'
                    }} className="flex items-center justify-center gap-2 text-violet-400 font-bold" data-id="element-141">
                        <span className="text-xl" data-id="element-142">🔥</span>
                        <span data-id="element-143">
                          ¡Últimos{' '}
                          <motion.span key={spots} initial={{
                          scale: 1.5,
                          color: '#ef4444'
                        }} animate={{
                          scale: 1,
                          color: 'inherit'
                        }} transition={{
                          duration: 0.5,
                          type: 'spring'
                        }} className="inline-block" data-id="element-144">
                            {spots}
                          </motion.span>{' '}
                          cupos en oferta!
                        </span>
                      </motion.div>

                      <div className="w-full max-w-sm space-y-3" data-id="element-145">
                        <div className="flex items-center justify-between text-[10px] font-black text-violet-400 uppercase tracking-widest" data-id="element-146">
                          <span className="flex items-center gap-1" data-id="element-147">
                            <Zap size={12} className="text-amber-500" data-id="element-148" />
                            La oferta expira en:
                          </span>
                          <span className="text-violet-950 font-mono text-sm" data-id="element-149">
                            {formatTime(timeLeft)}
                          </span>
                        </div>

                        <div className="h-2 w-full bg-violet-100 rounded-full overflow-hidden" data-id="element-150">
                          <motion.div className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500" initial={{
                          width: '100%'
                        }} animate={{
                          width: `${timeLeft / 900 * 100}%`
                        }} transition={{
                          duration: 1,
                          ease: 'linear'
                        }} data-id="element-151" />
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 -mx-8 md:-mx-12" data-id="element-152"></div>
                  </div>
                </div>

                {/* Trust Section */}
                <div className="p-8 md:p-12 bg-violet-50/50 border-t-2 border-dashed border-violet-100 space-y-[5px]" data-id="element-153">
                  {/* Primary Trust Signals - Large and Stacked */}
                  <div className="text-center space-y-[0px]" data-id="element-154">
                    <img src="https://cdn.magicpatterns.com/uploads/skccFFLzCHBogMzzjkfEmF/7ef4071d-c511-4325-9d9d-f2158f7b8632.jpg" alt="MercadoLíder Platinum" className="h-40 md:h-56 mx-auto object-contain shadow-md rounded-2xl" data-id="element-155" />
                    <img src="https://cdn.magicpatterns.com/uploads/eU118ieSDgWuz64T6oQtAx/capture_260410_163646.png" alt="Pago Seguro Garantizado" className="w-full max-w-[450px] mx-auto object-contain" data-id="element-156" />
                  </div>

                  {/* Secondary Trust Signals - Row */}
                  <div className="border-t border-violet-50 pt-[0px] mt-[0px]" data-id="element-157">
                    <div className="flex md:gap-20 gap-[0px] flex-row items-center justify-center" data-id="element-158">
                      <img src="https://cdn.magicpatterns.com/uploads/7eCtKk7WA3cLyGaeie1cdM/png-clipart-safe-purchase-payment-security-shop-shopping-online-store-e-commerce-sign-green-texta.png" alt="Compra Segura" className="h-20 md:h-24 object-contain" data-id="element-159" />
                      <img src="https://cdn.magicpatterns.com/uploads/fpbSfdYZaTGuGJTHBKUizP/Selo_de_Garantia_de_7_Dias_PNG_Transparente_Sem_Fundo.png" alt="Garantía de 7 Días" className="h-24 md:h-32 object-contain" data-id="element-160" />
                    </div>
                  </div>
                </div>
                <div className="-mx-[6px] -mb-[6px]" data-id="element-161">
                  <img src="https://cdn.magicpatterns.com/uploads/bGRSMW1E885vkqFEz2NPMr/hf_20260421_033200_14c7894a-e9fd-4cbb-8525-94fee542402f.webp" alt="Regalos Exclusivos Solo Por Hoy" className="w-full h-auto" data-id="element-162" />
                </div>
              </div>
            </div>
          </div>
          <LifetimeAccessBonus data-id="element-163" />
        </div>
      </section>

      {/* Creator Section */}
      <section className="bg-white" data-id="element-164">
        <img src="https://cdn.magicpatterns.com/uploads/pg1kCztc7zHcL3jv1atLar/hf_20260519_161535_82271015-bcf6-4ad9-8067-cafc6dcb7aad.png" alt="Sofía Alondra - Creadora de la Guía Reiki Terapéutico" className="w-full h-auto block" data-id="element-165" />
      </section>

      <Reviews data-id="element-166" />

      <FAQ data-id="element-167" />

      <footer className="py-12 border-t border-violet-100 text-center text-violet-600 text-sm" data-id="element-168">
        <p data-id="element-169">© 2025 Guía Reiki Terapéutico. Todos los derechos reservados.</p>
      </footer>
    </div>;
}